import { useState } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, FileDown, ClipboardList, Loader2, GripVertical, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { getAiProviderArgs } from '../lib/aiProviderPicker';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import SaveSnapshotButton from './SaveSnapshotButton';
import { printHtml } from '../lib/print';
import DocumentHeader from './DocumentHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ChecklistCreator — reusable Hyatt-branded checklist builder.
   Lives inside any workspace dialog (HR, Engineering, F&B, etc.) and
   inside the Property Budgets dialog so editors can spin a polished
   checklist into PDF / Excel / CSV in a single dialog session.
   Props:
     • open / onClose          — controlled by the parent
     • workspaceLabel          — appears in the export footer (e.g. "HR Projects · Onboarding")
     • adminToken              — required for the export API call
     • initialItems (optional) — preload from a template */
export default function ChecklistCreator({ open, onClose, workspaceLabel, adminToken, initialItems = [] }) {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [items, setItems] = useState(initialItems.length ? initialItems : [{ text: '', checked: false, note: '' }]);
  const [exporting, setExporting] = useState(null); // 'pdf' | 'xlsx' | 'csv' | null

  if (!open) return null;

  const updateItem = (idx, field, value) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  };

  const addItem = () => setItems((arr) => [...arr, { text: '', checked: false, note: '' }]);

  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const moveItem = (idx, direction) => {
    setItems((arr) => {
      const target = idx + direction;
      if (target < 0 || target >= arr.length) return arr;
      const next = [...arr];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const exportAs = async (fmt) => {
    if (!title.trim()) { toast.error('Give the checklist a title first.'); return; }
    const cleaned = items.filter((it) => (it.text || '').trim());
    if (cleaned.length === 0) { toast.error('Add at least one item.'); return; }
    setExporting(fmt);
    try {
      const res = await axios.post(`${API}/admin/checklists/export`, {
        title: title.trim(),
        subtitle: subtitle.trim(),
        workspace_label: workspaceLabel || '',
        items: cleaned.map((it) => ({ text: it.text.trim(), checked: !!it.checked, note: (it.note || '').trim() })),
        fmt,
      }, {
        headers: { Authorization: `Bearer ${adminToken}` },
        responseType: 'blob',
      });
      // Trigger download on the client.
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (title || 'checklist').replace(/[^A-Za-z0-9_\- ]/g, '').replace(/\s+/g, '_') || 'checklist';
      a.href = url;
      a.download = `${safe}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  return (
    /* Use Radix Dialog so this checklist modal is a SIBLING of any parent
       Radix Dialog (WorkspaceDialog / PropertyBudgetsDialog) rather than
       nested inside it. This is essential — when ChecklistCreator was a
       plain <div> portal'd to body, the parent Dialog still treated
       clicks on it as "outside" clicks (closing itself), AND its
       focus-trap stole keyboard focus back from the inputs. Radix
       Dialog primitives coordinate focus + outside-click correctly when
       multiple dialogs are stacked. */
    <Dialog open={!!open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent
        data-testid="checklist-creator"
        className="max-w-3xl p-0 overflow-hidden gap-0 bg-transparent border-0 shadow-none"
        style={{ backgroundColor: 'transparent' }}
      >
        <DialogTitle className="sr-only">Hyatt-branded checklist creator</DialogTitle>
        <DialogDescription className="sr-only">Build a Hyatt-branded checklist and export it as PDF, Excel, or CSV.</DialogDescription>
        <div className="w-full max-h-[90vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#FAF8F2', fontFamily: 'Manrope' }}>
          {/* Hyatt-branded header */}
          <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#A3D4D4' }}>
                <ClipboardList className="w-5 h-5" style={{ color: '#003DA5' }} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: '#A3D4D4' }}>Hyatt Inclusive Collection</p>
                <h2 className="text-lg font-bold text-white">Checklist Creator</h2>
                {workspaceLabel && (
                  <p className="text-[10px] text-white/70 mt-0.5">{workspaceLabel}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="checklist-print"
                title="Print this checklist"
                onClick={() => {
                  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                  const lis = items.map((it) => {
                    const text = typeof it === 'string' ? it : (it?.text || '');
                    const done = typeof it === 'object' ? !!it.checked : false;
                    return `<li>${done ? '☑' : '☐'} ${esc(text)}</li>`;
                  }).join('');
                  const body = `
                    <p class="kicker">DREAMS ROSE HALL · ${esc((workspaceLabel || '').toUpperCase())}</p>
                    <h1>${esc(title || 'Checklist')}</h1>
                    ${subtitle ? `<p>${esc(subtitle)}</p>` : ''}
                    <ul>${lis}</ul>
                  `;
                  printHtml(body, title || 'Checklist');
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-white"
                style={{ backgroundColor: 'rgba(255,255,255,0.18)', fontFamily: 'Manrope' }}
              >
                <Printer className="w-3 h-3" /> Print
              </button>
              <SaveSnapshotButton
                kind="checklist"
                defaultTitle={title ? `${title} · ${new Date().toLocaleDateString()}` : `Checklist · ${new Date().toLocaleDateString()}`}
                getPayload={() => ({ title, subtitle, items, workspaceLabel })}
                testid="checklist-save-snapshot"
              />
              <button data-testid="checklist-close" onClick={onClose} className="p-2 rounded-lg text-white hover:bg-white/10 transition">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

        {/* Title + subtitle */}
        <div className="px-6 pt-4">
          {/* Centered branded banner. The title input below is the
              persistent document name — we keep it (rather than using
              DocumentHeader's input) because the export API takes a
              `title` field, and the existing field is already wired. */}
          <DocumentHeader
            scopeId={`checklist.${(workspaceLabel || 'global').replace(/[^A-Za-z0-9_-]+/g, '-')}`}
            autoName={title || 'Checklist'}
            deptLabel={workspaceLabel || ''}
            onChange={(t) => { if (!title && t && t !== 'Checklist') setTitle(t); }}
            readOnly={false}
          />
        </div>
        <div className="px-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Checklist title" required>
            <input
              data-testid="checklist-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Pre-Opening Walkthrough"
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
              style={{ borderColor: '#D6D0C0' }}
            />
          </Field>
          <Field label="Subtitle (optional)">
            <input
              data-testid="checklist-subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="e.g. Lobby · Spa · Pool deck"
              className="w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2"
              style={{ borderColor: '#D6D0C0' }}
            />
          </Field>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#003DA5' }}>Items</p>
            <span className="text-[10px]" style={{ color: '#5C6B6B' }}>{items.length} task{items.length === 1 ? '' : 's'}</span>
          </div>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <div
                key={`${idx}`}
                data-testid={`checklist-row-${idx}`}
                className="flex items-start gap-2 p-3 rounded-xl"
                style={{ backgroundColor: 'white', border: '1px solid #E2DBC8' }}
              >
                <div className="flex flex-col items-center gap-0.5 pt-0.5">
                  <button
                    onClick={() => moveItem(idx, -1)}
                    disabled={idx === 0}
                    className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                    title="Move up"
                  >
                    <GripVertical className="w-3 h-3" style={{ color: '#5C6B6B' }} />
                  </button>
                  <span className="text-[10px] font-bold" style={{ color: '#5C6B6B' }}>{idx + 1}</span>
                </div>
                <input
                  data-testid={`checklist-item-checked-${idx}`}
                  type="checkbox"
                  checked={!!it.checked}
                  onChange={(e) => updateItem(idx, 'checked', e.target.checked)}
                  className="mt-1.5 w-4 h-4 rounded"
                  style={{ accentColor: '#003DA5' }}
                />
                <div className="flex-1">
                  <input
                    data-testid={`checklist-item-text-${idx}`}
                    value={it.text}
                    onChange={(e) => updateItem(idx, 'text', e.target.value)}
                    placeholder="Task description"
                    className="w-full px-2 py-1 rounded text-sm border-0 bg-transparent outline-none border-b border-transparent focus:border-blue-300"
                    style={{ color: '#1A2E2E' }}
                  />
                  <input
                    data-testid={`checklist-item-note-${idx}`}
                    value={it.note}
                    onChange={(e) => updateItem(idx, 'note', e.target.value)}
                    placeholder="Note · target · responsible person"
                    className="w-full mt-1 px-2 py-1 rounded text-[11px] border-0 bg-transparent outline-none border-b border-transparent focus:border-blue-300"
                    style={{ color: '#5C6B6B' }}
                  />
                </div>
                <button
                  data-testid={`checklist-item-remove-${idx}`}
                  onClick={() => removeItem(idx)}
                  className="p-1.5 rounded hover:bg-red-50"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
                </button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              data-testid="checklist-add-item"
              onClick={addItem}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold"
              style={{ backgroundColor: '#A3D4D420', color: '#003DA5' }}
            >
              <Plus className="w-3.5 h-3.5" /> Add item
            </button>
            <button
              data-testid="checklist-ai-generate"
              onClick={async () => {
                const purpose = window.prompt('What is the checklist for? (e.g. "Daily restaurant opening checks")', title || '');
                if (!purpose) return;
                const styleChoice = window.prompt('Style? simple | metric | weighted | inspection', 'simple');
                if (!styleChoice) return;
                const count = parseInt(window.prompt('How many items? (default 12)', '12'), 10) || 12;
                const providerArgs = await getAiProviderArgs();
                if (providerArgs === null) return; // user cancelled
                try {
                  const { data } = await axios.post(`${API}/admin/ai/generate-checklist`, {
                    title: purpose,
                    purpose,
                    department: workspaceLabel || null,
                    item_count: count,
                    style: styleChoice,
                    ...providerArgs,
                  }, { headers: { Authorization: `Bearer ${adminToken}` } });
                  if (data?.title && !title) setTitle(data.title);
                  if (data?.description && !subtitle) setSubtitle(data.description);
                  const incoming = (data?.items || []).map((it) => ({
                    text: [it.label, it.metric && `· target: ${it.metric}`, it.severity && `[${it.severity}]`, it.weight && `(weight ${it.weight})`].filter(Boolean).join(' '),
                    checked: false,
                    note: it.notes || '',
                  }));
                  setItems((arr) => {
                    const base = arr.filter((it) => (it.text || '').trim());
                    return [...base, ...incoming];
                  });
                  toast.success(`AI drafted ${incoming.length} items. Edit & export when ready.`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'AI checklist generation failed.');
                }
              }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', boxShadow: '0 4px 12px rgba(139,92,246,0.35)' }}
              title="Draft this checklist with AI — pick a style (simple, metric, weighted, inspection)"
            >
              ✨ Draft with AI
            </button>
          </div>

          {/* Summary totals */}
          {items.length > 0 && (() => {
            const filled = items.filter((it) => (it.text || '').trim()).length;
            const done = items.filter((it) => it.checked && (it.text || '').trim()).length;
            const pct = filled > 0 ? Math.round((done / filled) * 100) : 0;
            return (
              <div
                data-testid="checklist-totals"
                className="mt-4 p-3 rounded-xl flex flex-wrap items-center gap-4 text-xs"
                style={{ backgroundColor: '#EAF2FF', border: '1px solid #C9D6E6' }}
              >
                <span className="font-bold uppercase tracking-wider text-[10px]" style={{ color: '#003DA5' }}>Totals</span>
                <span style={{ color: '#1A2E2E' }}>Items: <strong>{filled}</strong></span>
                <span style={{ color: '#15803D' }}>Done: <strong>{done}</strong></span>
                <span style={{ color: '#DC2626' }}>Open: <strong>{filled - done}</strong></span>
                <span className="ml-auto px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#003DA5', color: 'white' }}>
                  {pct}% complete
                </span>
              </div>
            );
          })()}
        </div>

        {/* Export buttons */}
        <div className="px-6 py-3 flex flex-wrap items-center justify-between gap-2 border-t" style={{ borderColor: '#E2DBC8', backgroundColor: '#F5F0E1' }}>
          <p className="text-[11px]" style={{ color: '#5C6B6B' }}>
            Export your checklist as
          </p>
          <div className="flex items-center gap-2">
            {[
              { id: 'pdf',  label: 'PDF',   color: '#DC2626' },
              { id: 'xlsx', label: 'Excel', color: '#15803D' },
              { id: 'csv',  label: 'CSV',   color: '#1F4FBE' },
            ].map((f) => (
              <button
                key={f.id}
                data-testid={`checklist-export-${f.id}`}
                onClick={() => exportAs(f.id)}
                disabled={!!exporting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white transition hover:-translate-y-0.5 disabled:opacity-50"
                style={{ backgroundColor: f.color }}
              >
                {exporting === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                {f.label}
              </button>
            ))}
          </div>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#003DA5' }}>
        {label}{required && <span style={{ color: '#DC2626' }}> *</span>}
      </span>
      {children}
    </label>
  );
}
